"use client";

import { useId, useMemo } from "react";
import { ComposedChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import type { PricePoint, YearValue } from "@/lib/data/types";
import { computeDailyDividendYield } from "@/lib/dividend";
import { formatPercent } from "@/lib/format";
import {
  downsample,
  filterDateRange,
  withTimeField,
  computeNiceYAxisTicks,
  computePriceAxisTicks,
  formatPriceAxisTick,
} from "@/lib/price-chart-utils";
import type { ChartRangeLabel } from "@/components/charts/range-selector";
import { useIsNarrowViewport } from "@/lib/use-narrow-viewport";

const GREEN = "#3F7A5B";

interface Row {
  time: number;
  historicalValue?: number;
}

function formatTooltipDate(value: string | number): string {
  return new Date(value).toLocaleDateString("de-DE", { year: "numeric", month: "short", day: "2-digit" });
}

/**
 * Tagesgenaue Dividendenrendite-HISTORIE (Feedback-Runde 4, Punkt 10) - ersetzt die
 * vorherige rein jährliche `DividendYieldTimelineChart`. Gleiches Muster wie
 * `FairValueSimulator`/`DailyMultiplesChart`: echter Kurs-Zeitstempel als numerische
 * Achse, historische Rendite dicht/taeglich. `range` kommt bewusst von außen (kein
 * eigener RangeSelector hier) - die bereits bestehende Hochrechnungszeitraum-Auswahl in
 * `DividendProjection` steuert gleichzeitig auch die History-Anzeige, statt eine zweite,
 * redundante Zeitraum-Pille zu zeigen (dieselbe Konsolidierung wie beim
 * FairValueSimulator, Runde 4 Punkt 5).
 *
 * **[Punkt 57] (2026-08-11)**: zeigte bis dahin zusaetzlich eine gestrichelt an die
 * Historie anschliessende Hochrechnungs-Linie (`projection`-Prop) - Erik empfand das als
 * Vermischung von "Rendite-Verlauf" (Ist) und "Hochrechnung" (Prognose), die als zwei
 * getrennte Modi im uebergeordneten `DividendProjection` ohnehin schon existieren. Prop +
 * Bruecken-Logik komplett entfernt, dieser Chart zeigt jetzt ausschliesslich Historie -
 * die Hochrechnung bleibt exklusiv im "Hochrechnung"-Tab (`HistoricalLineChart` dort).
 */
export function DailyDividendYieldChart({
  priceHistory,
  dividendPerShare,
  name,
  range,
}: {
  priceHistory: PricePoint[];
  dividendPerShare: YearValue[];
  name: string;
  range: ChartRangeLabel;
}) {
  const gradientId = `dailyDividendYieldFill-${useId()}`;
  const isNarrow = useIsNarrowViewport();

  const dailyYield = useMemo(
    () => computeDailyDividendYield(priceHistory.map((p) => ({ date: p.date, value: p.close })), dividendPerShare),
    [priceHistory, dividendPerShare],
  );

  const filtered = useMemo(
    () => filterDateRange(dailyYield.map((d) => ({ date: d.date, close: d.yield }) as PricePoint), range),
    [dailyYield, range],
  );
  const renderedHistorical = useMemo(() => withTimeField(downsample(filtered, 1000, (p) => p.close)), [filtered]);

  const chartData = useMemo<Row[]>(
    () => renderedHistorical.map((p) => ({ time: p.time, historicalValue: p.close })),
    [renderedHistorical],
  );

  // Ersetzt Rechartsʼ Auto-Tick-Heuristik, die den obersten Tick teils nicht auf den
  // gleichen Rhythmus wie die uebrigen Ticks rundete (backlog.md -> Runde 5 Punkt 19).
  const yAxisTicks = useMemo(
    () => computeNiceYAxisTicks(chartData.map((d) => d.historicalValue).filter((v): v is number => v !== undefined), false),
    [chartData],
  );

  const axisTicks = useMemo(
    () =>
      computePriceAxisTicks(
        chartData.map((row) => ({ date: new Date(row.time).toISOString() })),
        range,
        isNarrow ? 5 : 8,
      ).map((d) => Date.parse(d)),
    [chartData, range, isNarrow],
  );

  if (dailyYield.length === 0) {
    return <p className="text-sm text-muted-foreground">Für diese Aktie liegt keine Dividendenrendite-Historie vor.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 12, right: 8, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GREEN} stopOpacity={0.22} />
                <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              ticks={axisTicks}
              interval={0}
              tickFormatter={(v: number) => formatPriceAxisTick(v, range)}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => formatPercent(v)}
              width={56}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value) => {
                if (value === undefined || value === null) return [null, null];
                return [formatPercent(Number(value)), name];
              }}
              labelFormatter={(label) => formatTooltipDate(Number(label))}
              cursor={{ stroke: "#ECEAE3" }}
              contentStyle={{ borderRadius: 4, borderColor: "#ECEAE3", fontSize: 13 }}
            />
            <Area
              type="linear"
              dataKey="historicalValue"
              stroke={GREEN}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: GREEN, stroke: "#FAF9F5", strokeWidth: 2 }}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
